import { z } from 'zod';
import { googleKeywordTextSchema } from './google-keyword.js';
// Google Ads write proposals (s200 — Phase B).
//
// Each schema below shapes a single founder-approved mutation. Magig's
// API endpoints accept these on the wire; the RealGoogleAdsClient
// translates them into the corresponding Google Ads API operation.
//
// Conventions:
//   - Money is USD whole or fractional dollars on the wire; the client
//     multiplies by 1_000_000 to get the Google Ads `*_micros` shape.
//   - IDs use Google's `customers/<n>/<resource>/<id>` resource-name
//     pattern internally, but we accept the bare numeric id from the
//     dashboard and reassemble. The API endpoints take both customerId
//     + the resource id separately.
//   - Every proposal is the founder-approved spec; the dashboard owns
//     the approval gate. The api endpoint accepts the proposal verbatim
//     and executes it.
// ─── Status changes ──────────────────────────────────────────────
/** Pause or enable a campaign or ad_group. Same shape because both
 *  expose a simple `{customer_id, resource_id}` path. Ads use a
 *  separate schema (googleAdsAdStatusChangeSchema below) because
 *  their resource name is compound (`{ad_group_id}~{ad_id}`).
 *  REMOVED is intentionally not available HERE, and that is now a
 *  narrower statement than it used to be. The rule was "Magig never
 *  removes resources (audit trail concerns)"; decision 261f69c
 *  overturned it for exactly one caller — the founder's own delete of a
 *  Magig campaign, which removes on Google so the spend stops for good.
 *  That path goes through GoogleAdsClient.removeCampaign, a separate
 *  method taking a `remove` operation, specifically so this schema can
 *  stay ENABLED|PAUSED: autopilot, the chat tools and the takeover
 *  action kinds all speak through this shape and none of them may reach
 *  removal. The audit-trail concern is answered by the other half of
 *  that decision — the campaign row is ARCHIVED rather than deleted, so
 *  the history survives in Magig, which is where the founder reads it. */
export const googleAdsStatusChangeSchema = z.object({
    /** Numeric id of the campaign or ad_group. */
    resourceId: z.string().regex(/^\d+$/),
    /** Target status. PAUSED + ENABLED are the only valid moves. */
    status: z.enum(['ENABLED', 'PAUSED']),
});
/** Pause/enable an individual ad. Distinct from the campaign/ad_group
 *  schema because Google's ad_group_ad resource names are compound:
 *  `customers/<cid>/adGroupAds/<ad_group_id>~<ad_id>`. Both ids must
 *  ride on the wire so the api can rebuild the resource name. */
export const googleAdsAdStatusChangeSchema = z.object({
    /** Numeric ad_group_ad id (the ad's own id). */
    resourceId: z.string().regex(/^\d+$/),
    /** Numeric id of the parent ad_group. */
    adGroupResourceId: z.string().regex(/^\d+$/),
    /** Target status. PAUSED + ENABLED are the only valid moves. */
    status: z.enum(['ENABLED', 'PAUSED']),
});
// ─── Campaign budget ─────────────────────────────────────────────
export const googleAdsBudgetUpdateSchema = z.object({
    /** Numeric id of the campaign_budget resource (NOT the campaign
     *  id — campaigns reference a budget; the budget is the mutable
     *  thing). The dashboard reads this off the snapshot. */
    budgetResourceId: z.string().regex(/^\d+$/),
    /** New daily budget in USD. Cap at 10k/day to catch typos
     *  (a real campaign over $10k/day should land via a deliberate
     *  separate flow, not an in-app slider). */
    dailyBudgetUsd: z.number().int().min(1).max(10_000),
});
// ─── Ad-group bid ────────────────────────────────────────────────
/** Spec 3c98725c — raise (or lower) the max CPC on an ad group.
 *
 *  AD-GROUP level, not per keyword, because that is where the bid
 *  actually lives today: every Mag-built campaign reports
 *  effective_cpc_bid_source = AD_GROUP. A per-keyword override is
 *  possible and introduces a second place a bid can come from, which
 *  makes "what am I bidding" ambiguous; not in v1. */
export const googleAdsAdGroupBidUpdateSchema = z.object({
    /** Numeric id of the ad_group resource. */
    adGroupResourceId: z.string().regex(/^\d+$/),
    /** New max CPC in USD. Bounded by the same [0.05, 20] range the
     *  campaign proposal accepts, so a health recommendation can never
     *  propose a bid the create path would have rejected. */
    cpcBidUsd: z.number().min(0.05).max(20),
});
// ─── Keyword bid ─────────────────────────────────────────────────
/** Spec a60ca841 — set (or clear) the max CPC on ONE keyword.
 *
 *  THIS REVERSES THE "not in v1" ABOVE, and the objection it was refused on
 *  was a fair one, so it is worth answering rather than stepping around. The
 *  ad-group schema argues a per-keyword override "introduces a second place a
 *  bid can come from, which makes 'what am I bidding' ambiguous". True. What
 *  changed is that the single group bid turned out to be WRONG rather than
 *  merely coarse: inside gestionalo's one serving ad group, google's own
 *  first-page estimates span $0.02 to $1.29 — 64x — and the uniform $1.00
 *  group bid already overshoots two of its four keywords, both of which
 *  already hold 100% impression share. There is no single value that serves
 *  them, so the choice is not "one clear bid vs two ambiguous ones", it is
 *  "one wrong bid vs the right bid per keyword".
 *
 *  And the ambiguity has an answer google already provides:
 *  `effective_cpc_bid_micros` is the one bid in force, resolved by google and
 *  read directly by the health path. "What am I bidding" is that field, which
 *  is true whether or not an override exists.
 *
 *  Clearing is first-class, not an afterthought. `cpcBidUsd: null` removes the
 *  override and returns the keyword to the group max — which is the REVERT
 *  path, and the only one needed today: every enabled keyword on the fleet
 *  currently has its own bid UNSET (181 criterion rows, zero overrides), so
 *  the way back from a first raise is "clear it", not "restore a remembered
 *  number". Both directions are accepted by google (theory e7fce15). */
export const googleAdsKeywordBidUpdateSchema = z.object({
    adGroupResourceId: z.string().regex(/^\d+$/),
    criterionResourceId: z.string().regex(/^\d+$/),
    /** New max CPC for this keyword alone, or null to drop the override and
     *  fall back to the ad group's. Bounded by the same [0.05, 20] range as the
     *  ad-group bid and the campaign proposal, so no path can set a per-keyword
     *  bid the create path would have refused.
     *
     *  Named Usd and read by google in the ACCOUNT's currency, like every other
     *  *_micros field — see majorUnitsToMicros. The live fleet bills in USD, so
     *  the name is accurate today and would quietly stop being so on a
     *  non-USD account. */
    cpcBidUsd: z.number().min(0.05).max(20).nullable(),
});
// ─── Keyword status ──────────────────────────────────────────────
/** Spec 3c98725c — pause or re-enable one keyword.
 *
 *  PAUSE, never REMOVE. Removing is irreversible on Google's side and
 *  takes the keyword's history with it; pausing leaves the founder able
 *  to undo a call Mag recommended, which matters most for exactly the
 *  advice we are least sure of (see thesis T-1 on whether
 *  RARELY_SERVED is a verdict or a not-yet). */
export const googleAdsKeywordStatusChangeSchema = z.object({
    adGroupResourceId: z.string().regex(/^\d+$/),
    criterionResourceId: z.string().regex(/^\d+$/),
    status: z.enum(['ENABLED', 'PAUSED']),
});
// ─── Keyword capture ─────────────────────────────────────────────
/** [spec da48521a S-3] Keywords to ADD to an ad group that already serves
 *  their theme — the capture half of search-term mining.
 *
 *  EXACT IS NOT A FIELD. The spec settles the match type and the schema
 *  enforces it rather than carrying it: "it captures that precise search and
 *  nothing else, which is exactly the claim being made — you are paying for
 *  this search, bid on it directly". PHRASE and BROAD reach further than
 *  people expect, and on a POSITIVE that over-reach spends money rather than
 *  withholding traffic. A caller that wants another match type has to change
 *  this type, which is the point: it is a product decision, not a parameter.
 *
 *  NO BID. A captured keyword inherits the ad group's CPC. `updateKeywordCpcBid`
 *  exists for the separate decision of pricing one, and bundling the two would
 *  mean a founder reading one receipt about two changes.
 *
 *  Capped at the same ceiling as the exclusion action's term list, for the same
 *  reason: a tick that reshaped a campaign's targeting by twenty keywords is
 *  further than a founder can comfortably walk back inside one grace window. */
export const googleAdsNewKeywordsSchema = z.object({
    adGroupResourceId: z.string().regex(/^\d+$/),
    texts: z.array(z.string().min(1).max(200)).min(1).max(8),
});
// ─── Responsive search ad swap ───────────────────────────────────
/** Single asset (headline or description) on a responsive search ad.
 *  Google supports asset-level "pinning" (forcing a headline into
 *  position 1/2/3); we expose it so mag's recommendations can
 *  preserve brand-name pinning when swapping creative. */
export const rsaAssetSchema = z.object({
    text: z.string().min(1).max(120),
    /** 1, 2, or 3 to pin to that position. Undefined = let google
     *  rotate the asset freely. Only valid on headlines. */
    pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
});
export const googleAdsRsaSwapSchema = z.object({
    /** Numeric id of the ad_group_ad to replace. Google's policy is
     *  that responsive search ads can't be edited in place — to
     *  change copy you remove the old ad and create a new one. The
     *  client does both in one mutate call. */
    oldAdGroupAdResourceId: z.string().regex(/^\d+$/),
    /** Numeric id of the parent ad_group. Needed to create the
     *  replacement ad. */
    adGroupResourceId: z.string().regex(/^\d+$/),
    /** New RSA spec. Google requires 3-15 headlines, 2-4 descriptions. */
    headlines: z.array(rsaAssetSchema).min(3).max(15),
    descriptions: z
        .array(z.object({ text: z.string().min(1).max(90) }))
        .min(2)
        .max(4),
    /** The landing-page URL. Magig requires it explicitly even if the
     *  old ad had the same URL — the founder's approval surface should
     *  show what they're approving. */
    finalUrl: z.string().url(),
    /** Optional tracking template / suffix — leave undefined to inherit
     *  the parent. */
    trackingUrlTemplate: z.string().url().optional(),
});
// ─── New ad group ────────────────────────────────────────────────
export const googleAdsNewAdGroupSchema = z.object({
    /** Parent campaign id. */
    campaignResourceId: z.string().regex(/^\d+$/),
    /** Founder-facing name. Free text. */
    name: z.string().min(1).max(160),
    /** Initial CPC bid in USD. Max $20 — anything higher should land
     *  via a separate process with more friction. */
    cpcBidUsd: z.number().min(0.05).max(20),
    /** Initial keyword set. Optional — search-style ad groups need
     *  this to deliver, but the founder can also add keywords later. */
    keywords: z
        .array(z.object({
        text: googleKeywordTextSchema,
        matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
    }))
        .max(50)
        .optional(),
    /** Optional starting RSA — letting mag draft the ad group + first
     *  ad in one shot is the common pattern. */
    initialAd: z
        .object({
        headlines: z.array(rsaAssetSchema).min(3).max(15),
        descriptions: z
            .array(z.object({ text: z.string().min(1).max(90) }))
            .min(2)
            .max(4),
        finalUrl: z.string().url(),
    })
        .optional(),
});
// ─── New RSA on existing ad group ────────────────────────────────
export const googleAdsNewRsaSchema = z.object({
    adGroupResourceId: z.string().regex(/^\d+$/),
    headlines: z.array(rsaAssetSchema).min(3).max(15),
    descriptions: z
        .array(z.object({ text: z.string().min(1).max(90) }))
        .min(2)
        .max(4),
    finalUrl: z.string().url(),
    trackingUrlTemplate: z.string().url().optional(),
});
// ─── New campaign (full chain) ───────────────────────────────────
/** Bidding-strategy choice. The most common SMB picks. */
export const googleAdsBiddingStrategySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('MANUAL_CPC'),
        /** Enhanced CPC is deprecated on Search campaigns starting in
         *  Google Ads API v18 — setting this to true on a fresh create
         *  trips OPERATION_NOT_PERMITTED_FOR_CONTEXT. Default off; only
         *  flip it on when you've confirmed the target account still
         *  honors the legacy flag. */
        enhancedCpcEnabled: z.boolean().default(false),
    }),
    z.object({
        kind: z.literal('MAXIMIZE_CONVERSIONS'),
        /** Optional target CPA (USD). Without it, google maximizes
         *  conversions for the budget without a cost ceiling. */
        targetCpaUsd: z.number().min(1).max(1000).optional(),
    }),
    z.object({
        kind: z.literal('TARGET_SPEND'),
        /** Optional max-cpc ceiling (USD). */
        cpcBidCeilingUsd: z.number().min(0.05).max(50).optional(),
    }),
]);
/** Channel type — what kind of campaign. The dashboard surface
 *  currently shows Search + Performance Max + Display; Video +
 *  Shopping are valid Google Ads types we support on the wire but
 *  the founder UI doesn't yet expose them. */
export const googleAdsChannelTypeSchema = z.enum([
    'SEARCH',
    'PERFORMANCE_MAX',
    'DISPLAY',
    'SHOPPING',
    'VIDEO',
]);
/** Where a campaign's ads may serve. The two "partner" fields are NOT
 *  interchangeable and confusing them put every campaign on partner
 *  inventory for a month [founder-reported 2026-10-28].
 *
 *  Quoting Campaign.NetworkSettings in the Google Ads API reference:
 *
 *    target_search_network         "Whether ads will be served on sites in
 *                                   the Google Search PARTNERS Network
 *                                   (requires targetGoogleSearch to also be
 *                                   true)."
 *    target_partner_search_network "available only to some select partner
 *                                   accounts. ... This does NOT control
 *                                   whether ads will be served on Google
 *                                   Search Partners Network; use
 *                                   target_search_network for that instead."
 *
 *  So `targetSearchNetwork` IS Search Partners, and the restricted field
 *  below is not the protection its name suggests. Partners defaults OFF:
 *  that inventory includes third-party apps and in-app webviews, and on
 *  magig it delivered 167 of 215 paid sessions inside the Facebook browser
 *  at 22.5% average scroll with no account ever created.
 *
 *  Still expressible as true — a founder who wants partner reach may ask
 *  for it. It simply must not arrive by default under a comment claiming
 *  the campaign is search-only. */
export const googleAdsNetworkSettingsSchema = z.object({
    targetGoogleSearch: z.boolean().default(true),
    targetSearchNetwork: z.boolean().default(false),
    targetContentNetwork: z.boolean().default(false),
    targetPartnerSearchNetwork: z.boolean().default(false),
});
/** A Mag-authored, founder-approved campaign proposal. Drives the
 *  5-step create chain (budget → campaign → ad group → ad →
 *  keywords). Treated as the source of truth for the explicit
 *  "launch this campaign" click in the dashboard. */
export const googleAdsCampaignProposalSchema = z.object({
    /** Founder-facing campaign name. Magig prepends "magig — " in the
     *  client to make it obvious in the customer's Google Ads UI
     *  which campaigns we shipped. */
    name: z.string().min(1).max(160),
    channelType: googleAdsChannelTypeSchema,
    /** Daily budget USD. Same cap as the budget-update endpoint. */
    dailyBudgetUsd: z.number().int().min(1).max(10_000),
    biddingStrategy: googleAdsBiddingStrategySchema,
    networkSettings: googleAdsNetworkSettingsSchema,
    /** ISO date (YYYY-MM-DD) for when the campaign starts. Defaults
     *  to today in the dashboard. */
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Optional end date — most SMB founders set this to control
     *  spend cap. */
    endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** First ad group + ad + keyword set. Search campaigns need this
     *  to deliver. Performance Max campaigns use a different
     *  "asset group" shape that v1 doesn't yet support — those
     *  return a clear 501 from the api. */
    adGroup: z.object({
        name: z.string().min(1).max(160),
        cpcBidUsd: z.number().min(0.05).max(20),
        headlines: z.array(rsaAssetSchema).min(3).max(15),
        descriptions: z
            .array(z.object({ text: z.string().min(1).max(90) }))
            .min(2)
            .max(4),
        finalUrl: z.string().url(),
        keywords: z
            .array(z.object({
            text: googleKeywordTextSchema,
            matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
        }))
            .min(1)
            .max(50),
    }),
    /** Campaign-level exclusions — spec `preflight-the-plan` S4.
     *
     *  Applied as campaign_criterion rows with negative=true, AFTER the create
     *  chain's five steps. Last on purpose: callers read the campaign's resource
     *  name out of `resourceNames[1]`, and anything inserted earlier would shift
     *  what they parse.
     *
     *  Optional, unlike `targetCountries` above. The asymmetry is deliberate and
     *  it is not an inconsistency: a campaign with no location criterion serves
     *  WORLDWIDE, so absence there means the worst possible thing and must be
     *  impossible to express. A campaign with no negative criteria excludes
     *  nothing, which is merely the status quo every campaign launched before
     *  this field already had. */
    negativeKeywords: z
        .array(z.object({
        text: googleKeywordTextSchema,
        matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
    }))
        .max(50)
        .optional(),
    /** Monthly spend ceiling in USD — informational, NOT enforced by
     *  google. Magig stores it on the imported_campaigns row so the
     *  founder sees a separate alert if cumulative spend approaches
     *  the cap. */
    monthlySpendCeilingUsd: z.number().int().min(1).max(300_000).optional(),
    /** ISO 3166-1 alpha-2 country codes the campaign may serve in.
     *
     *  REQUIRED, and `.min(1)` is the point of it. A google campaign
     *  created with no location criterion serves WORLDWIDE, and that is
     *  the worst-shaped failure this product can produce: it spends, it
     *  accrues clicks, the dashboard reads green, and most of the budget
     *  reaches people who cannot buy. Campaign 24254112417 ran that way
     *  until a founder happened to open the Ads UI for an unrelated
     *  reason (decision 62d8b48).
     *
     *  Being required rather than optional is deliberate: it makes
     *  "launch with no market" a compile error at every construction
     *  site instead of a runtime check somebody forgets to call. Absence
     *  must never quietly mean "everywhere" when it is the founder's
     *  money. */
    targetCountries: z
        .array(z.string().regex(/^[A-Z]{2}$/, 'expected an ISO 3166-1 alpha-2 country code'))
        .min(1)
        .max(30),
});
// ─── Write results ───────────────────────────────────────────────
/** Result of any write mutation. Includes the Google Ads resource
 *  ids that were touched, so the dashboard can re-render with the
 *  new state without a full re-fetch. */
export const googleAdsMutationResultSchema = z.object({
    /** Resource names of the mutated/created resources, in the order
     *  the client issued the operations. Format:
     *  "customers/<n>/campaigns/<n>", etc. */
    resourceNames: z.array(z.string().min(1).max(200)),
    /** Whether the mutation actually hit Google's API ('meta') or
     *  was a mock ('mock'). Lets the dashboard toast distinguish
     *  "really paused" vs "mock paused". */
    via: z.enum(['mock', 'google']),
});
//# sourceMappingURL=google-ads-writes.js.map